import React from 'react'
import { useState, useEffect } from 'react';
import {IOneplaceLibraryContextProp, withOneplaceLibraryContext} from 'oneplace-components';
import {
    Checkbox, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Button,
    Typography, InputLabel, FormHelperText
} from '@material-ui/core';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import TextChipListControl from '../form_controls/TextChipListControl';

// TextChipListControl hardcodes `marginTop: 20` (inline style) on its own root div and accepts
// no style/className prop to override it. An inline style always beats a plain external
// stylesheet rule regardless of selector specificity, so overriding it from here needs an
// `!important` JSS rule - targeting the control's (class-less) root div structurally via a `&
// > div` child selector on a wrapper we do control, rather than touching the component itself.
const styles = createStyles({
    emailChipsWrapper: {
        '& > div': {
            marginTop: '5px !important'
        }
    },
    includePDFCheckbox: {
        marginTop: '10px'
    },
    emailInputLabel: {marginTop: '10px', marginBottom: '0 !important' }
});

export type IncidentSubmissionStatus = null | 'submitting' | 'success' | 'error'

export interface IIncidentSubmissionDialogProps extends IOneplaceLibraryContextProp, WithStyles<typeof styles> {
    status: IncidentSubmissionStatus
    content: string | React.ReactElement<any>
    contentDetail?: string | React.ReactElement<any>
    isOpen: boolean
    // readonly info fields shown during the confirm step, mirroring
    // SubmitChecklistDialog's assignee/checklist-name readonly fields.
    siteName: string
    surveyName: string
    // survey-level admin setting (CMA's "Allow incident PDF to be emailed" checkbox) - gates
    // whether the "Include incident PDF" checkbox is offered at all.
    allowAttachIncidentPdf: boolean
    onClose: () => void
    onConfirm: (emails: string, includePdf: boolean) => void
}

export const IncidentSubmissionDialog = withOneplaceLibraryContext(withStyles(styles)(
    (props: IIncidentSubmissionDialogProps) => {

    const [showCancelButton, setShowCancelButton] = useState<boolean>(false)
    const [showConfirmButton, setShowConfirmButton] = useState<boolean>(false)
    const [sendEmail, setSendEmail] = useState<boolean>(false)
    const [email, setEmail] = useState<string>('')
    const [includePdf, setIncludePdf] = useState<boolean>(false)

    // ticking the box with no address submitted happily and sent nothing - the backend reads a blank
    // copyToEmail as "no copy wanted"
    const emailMissing = sendEmail && email.trim().length === 0

    if(props.status === 'submitting' ){
        if(showCancelButton) setShowCancelButton(false)
        if(showConfirmButton) setShowConfirmButton(false)
    } else {
        if(!showCancelButton && props.status !== 'success') setShowCancelButton(true)
        if(!showConfirmButton) setShowConfirmButton(true)
    }

    useEffect( () => {
        if(!sendEmail && email.length > 0){
            setEmail('')
        }
        // "Include incident PDF" only ever applies once "Send incident confirmation" is
        // checked and the survey allows it - clear it whenever either stops being true, so a
        // stale checked state can't ride along after the checkbox is hidden again.
        if((!sendEmail || !props.allowAttachIncidentPdf) && includePdf){
            setIncludePdf(false)
        }
    },[sendEmail, email, includePdf, props.allowAttachIncidentPdf])

    return (
        <Dialog
            open={props.isOpen}
            onClose={props.onClose}
            PaperProps={{ style: { minWidth: '40%', maxWidth: 700}}}
        >
            <DialogTitle>Submit {props.ctx.i18next.t('customLabel_incident')}</DialogTitle>
            <DialogContent>
                {props.status === 'submitting' ?
                    <div style={{textAlign: 'center', width: '100%'}}>
                        <CircularProgress size={30} />
                    </div> : <Typography data-testid="dialog-content" style={{ whiteSpace: 'pre-line' }}>
                        {props.content}
                    </Typography>
                }
                {!props.status &&
                    <div>
                        <div style={{ marginTop: 10 }}>
                            <Typography variant="caption">{props.ctx.i18next.t('customLabel_site')}</Typography>
                            <Typography variant="subtitle1">{props.siteName}</Typography>
                        </div>
                        <div style={{ marginTop: 10 }}>
                            <Typography variant="caption">{props.ctx.i18next.t('customLabel_incident')}</Typography>
                            <Typography variant="subtitle1">{props.surveyName}</Typography>
                        </div>
                        <br/>
                        <FormControlLabel
                            control={<Checkbox color='primary'/>}
                            checked={sendEmail}
                            label={props.ctx.i18next.t('send_incident_confirmation')}
                            onChange={ (e: any) => {
                                setSendEmail(!sendEmail)
                            }}
                        />
                        {sendEmail &&
                            // Same chip-per-email UX as checklist's ChecklistEmailDialog, but
                            // without adopting Formik/a second dialog - TextChipListControl only
                            // needs the Formik-shaped {field, form, meta} props, so we hand it a
                            // small shim over this component's own `email` state (comma-joined
                            // string, same as before) and keep the single-request submit flow.
                            <>
                                <InputLabel className={props.classes.emailInputLabel} htmlFor="incidentEmailChips" shrink={true}>
                                    { `${props.ctx.i18next.t('send_email')} ${props.ctx.i18next.t('email_to')}`}
                                </InputLabel>
                                <div className={props.classes.emailChipsWrapper}>
                                    <TextChipListControl
                                        id="incidentEmailChips"
                                        label={props.ctx.i18next.t('add_email')}
                                        field={{ name: 'email', value: email } as any}
                                        form={{ setFieldValue: (_name: string, value: string) => setEmail(value) } as any}
                                        meta={{ value: null, touched: false, initialTouched: false } as any}
                                    />
                                </div>
                                {emailMissing &&
                                    <FormHelperText error={true} data-testid="incident-email-required">
                                        {props.ctx.i18next.t('email_required_for_confirmation')}
                                    </FormHelperText>
                                }
                                </>
                        }
                        {sendEmail && props.allowAttachIncidentPdf &&
                            <div className={props.classes.includePDFCheckbox}>
                                <FormControlLabel
                                    control={<Checkbox color='primary'/>}
                                    checked={includePdf}
                                    label={props.ctx.i18next.t('include_incident_pdf')}
                                    onChange={ (e: any) => {
                                        setIncludePdf(!includePdf)
                                    }}
                                />
                            </div>
                        }
                    </div>
                }
            </DialogContent>
            <DialogActions>
                {showCancelButton &&
                    <Button color='primary' onClick={props.onClose}>
                        Cancel
                    </Button>
                }
                {showConfirmButton  &&
                    <Button color='primary' disabled={emailMissing} onClick={() => props.onConfirm(email, includePdf)}>
                        OK
                    </Button>
                }
            </DialogActions>
        </Dialog>
    )
}))
